import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

export interface PlaygroundFilters {
  startDate?: string;
  endDate?: string;
  batchId?: number;
  courseId?: number;
  subjectTagId?: number;
}

export interface PlaygroundOverview {
  totalEvents: number;
  distinctStudents: number;
  resourceOpens: number;
  testLaunches: number;
  subjectOpens: number;
  modeStarts: number;
  distinctResources: number;
  distinctTests: number;
  totalAttempts: number;
  correctAttempts: number;
  accuracyPercent: number;
  distinctStudentsAttempted: number;
  distinctQuestions: number;
}

export interface PlaygroundAsset {
  kind: "resource" | "test" | "topic" | "other";
  objectId: number;
  name: string;
  verb: string;
  resourceTypeId: number | null;
  resourceTypeName: string | null;
  totalEvents: number;
  distinctStudents: number;
  totalTimeSpent: number;
  firstSeen: string;
  lastSeen: string;
}

export interface PlaygroundStudentRow {
  userId: number;
  enquiryId: number;
  studentName: string;
  loginName: string;
  totalEvents: number;
  resourceOpens: number;
  testLaunches: number;
  subjectOpens: number;
  distinctResources: number;
  totalAttempts: number;
  correctAttempts: number;
  accuracyPercent: number;
  lastActiveAt: string | null;
}

export interface PlaygroundTopicRow {
  tagId: number;
  tagName: string;
  tagTypeId: number | null;
  totalEvents: number;
  subjectOpens: number;
  modeStarts: number;
  distinctStudents: number;
  lastSeen: string | null;
}

export interface PlaygroundDrilldownRow {
  userId: number;
  studentName: string;
  loginName: string;
  verb: string;
  hits: number;
  firstSeen: string;
  lastSeen: string;
  totalTimeSpent: number;
}

class PlaygroundUsageService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/analytics/playground`;
  }

  private toQuery(filters: PlaygroundFilters & Record<string, any>): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
    });
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }

  async getOverview(filters: PlaygroundFilters): Promise<PlaygroundOverview> {
    const res = await myAxios.get(`${this.baseUrl}/overview${this.toQuery(filters)}`);
    return res.data;
  }

  async getTopAssets(
    filters: PlaygroundFilters & { kind?: "all" | "resource" | "test" | "topic"; limit?: number },
  ): Promise<{ assets: PlaygroundAsset[] }> {
    const res = await myAxios.get(`${this.baseUrl}/top-assets${this.toQuery(filters)}`);
    return res.data;
  }

  async getByStudent(
    filters: PlaygroundFilters & { limit?: number },
  ): Promise<{ students: PlaygroundStudentRow[] }> {
    const res = await myAxios.get(`${this.baseUrl}/by-student${this.toQuery(filters)}`);
    return res.data;
  }

  async getByTopic(
    filters: PlaygroundFilters & { limit?: number },
  ): Promise<{ topics: PlaygroundTopicRow[] }> {
    const res = await myAxios.get(`${this.baseUrl}/by-topic${this.toQuery(filters)}`);
    return res.data;
  }

  async getAssetDrilldown(
    kind: "resource" | "test" | "topic",
    assetId: number,
    filters: PlaygroundFilters & { limit?: number },
  ): Promise<{ kind: string; assetId: number; students: PlaygroundDrilldownRow[] }> {
    const res = await myAxios.get(
      `${this.baseUrl}/asset-drilldown/${kind}/${assetId}${this.toQuery(filters)}`,
    );
    return res.data;
  }

  async getTopActiveSteps(
    params: { days?: number; limit?: number; batchId?: number; courseId?: number },
  ): Promise<{ windowDays: number; steps: TopActiveStep[] }> {
    const res = await myAxios.get(
      `${Config.nodeSiteUrl}v1/analytics/top-active-steps${this.toQuery(params)}`,
    );
    return res.data;
  }
}

export interface TopActiveStep {
  stepId: number;
  stepName: string;
  lessonPlanId: number;
  lessonPlanName: string;
  batchId: number;
  batchName: string;
  eventCount: number;
  distinctStudents: number;
  lastSeen: string;
}

export default new PlaygroundUsageService();
